/**
 * RoadmapTab — multi-phase plan builder.
 *
 * Each phase references a snapshotted scenario + 3 overrides
 * (duration_months, num_tenants_override, users_per_tenant_override).
 * The backend `calculatePlan` engine produces a monthly cost timeline with
 * one-time setup amortization (each scenario's setup fires only on its
 * first appearance in the plan).
 */
import { useEffect, useMemo, useState } from 'react';
import { activeLocale } from '@/utils/locale';
import {
  Alert, Button, Card, Empty, InputNumber, Input, Modal, Slider,
  Spin, Statistic, Table, Tag, message, theme,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, FolderOpenOutlined, HolderOutlined } from '@ant-design/icons';
import {
  Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Legend,
  Line, ComposedChart,
} from 'recharts';
import { useLanguage } from '@/hooks/useLanguage';
import { ScenarioCard } from './ScenarioCard';
import { scenarioService } from '../services/scenarioService';
import { planService } from '../services/planService';
import { formatCurrency } from '../services/costEngine';
import type {
  PricingPayload, PlanPhase, PlanResult, SavedPlan, SavedScenario,
} from '../types';

interface Props {
  pricing: PricingPayload;
}

const fmt = (v: number) => formatCurrency(v);

/* localStorage key for the unsaved-plan draft. Survives tab switches and full
   reloads. Cleared automatically when the user saves the plan (because saving
   resets to an empty draft until they make new edits). */
const DRAFT_KEY = 'costSimulator.plan.draft.v1';

interface PlanDraft {
  name: string;
  currentPlanId: string | null;
  phases: PlanPhase[];
  savedAt: number;
}

function loadDraft(): PlanDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as PlanDraft;
    if (!Array.isArray(d.phases)) return null;
    return d;
  } catch { return null; }
}

function writeDraft(d: PlanDraft | null) {
  try {
    if (d === null || d.phases.length === 0) localStorage.removeItem(DRAFT_KEY);
    else localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch { /* localStorage may be unavailable in private mode */ }
}

export function RoadmapTab({ pricing }: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  // ── State ────────────────────────────────────────────────────────────────
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [plans, setPlans] = useState<SavedPlan[]>([]);
  const [phases, setPhases] = useState<PlanPhase[]>([]);
  const [planName, setPlanName] = useState('');
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [result, setResult] = useState<PlanResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [scenarioPickerOpen, setScenarioPickerOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  /* Drag-and-drop state for reordering phases */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // ── Load saved scenarios + plans on mount ────────────────────────────────
  useEffect(() => {
    setLoadingScenarios(true);
    Promise.all([scenarioService.list(), planService.list()])
      .then(([s, p]) => { setScenarios(s); setPlans(p); })
      .catch(() => {})
      .finally(() => setLoadingScenarios(false));

    // Restore any unsaved-plan draft from a previous session silently —
    // the user found the toast noisy (especially in StrictMode dev where
    // effects fire twice). The Unsaved-plan hint near the Save button
    // already conveys "this isn't persisted yet" if it matters.
    const draft = loadDraft();
    if (draft && draft.phases.length > 0) {
      setPhases(draft.phases);
      setPlanName(draft.name);
      setCurrentPlanId(draft.currentPlanId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-persist the current draft (debounced) so a tab switch / reload
  //    doesn't lose work. Saved plans are also kept in their server row but the
  //    in-progress edits live here until the user clicks Save. ─────────────
  useEffect(() => {
    const handle = setTimeout(() => {
      writeDraft({
        name: planName, currentPlanId, phases, savedAt: Date.now(),
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [phases, planName, currentPlanId]);

  // ── Re-calculate whenever phases change (debounced server call) ──────────
  useEffect(() => {
    if (phases.length === 0) { setResult(null); return; }
    let cancelled = false;
    setCalculating(true);
    const handle = setTimeout(async () => {
      try {
        const r = await planService.calculate(phases);
        if (!cancelled) setResult(r);
      } finally {
        if (!cancelled) setCalculating(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [phases]);

  // ── Phase mutations ──────────────────────────────────────────────────────
  function addPhase(s: SavedScenario) {
    // Tolerate two scenario shapes:
    //   - Advanced (legacy): { agents: [{activeUsersPerMonth}], numSubTenants }
    //   - Simple Simulator: { _kind:'simple', agents: [...], active_users, num_tenants }
    const p: any = s.parameters || {};
    const isSimple = p._kind === 'simple';
    const defaultTenants = isSimple
      ? (p.num_tenants ?? 1)
      : (p.numSubTenants ?? 1);
    const defaultUsersPerTenant = isSimple
      ? Math.max(1, Math.round(p.active_users ?? 100))
      : (() => {
          const totalActive = (p.numSubTenants ?? 1)
            * (p.agents?.reduce((sum: number, a: any) => sum + (a.activeUsersPerMonth ?? 0), 0) ?? 0);
          return Math.max(1, Math.round(totalActive / Math.max(1, p.numSubTenants ?? 1)));
        })();
    setPhases(prev => [...prev, {
      id: crypto.randomUUID(),
      scenario_id: s.scenario_id,
      scenario_snapshot: s.parameters,
      scenario_name_at_snapshot: s.name,
      duration_months: 6,
      num_tenants_override: defaultTenants,
      users_per_tenant_override: defaultUsersPerTenant,
      label: `Phase ${prev.length + 1}`,
    }]);
    setScenarioPickerOpen(false);
  }
  function updatePhase(idx: number, partial: Partial<PlanPhase>) {
    setPhases(prev => prev.map((p, i) => i === idx ? { ...p, ...partial } : p));
  }
  function removePhase(idx: number) {
    setPhases(prev => prev.filter((_, i) => i !== idx));
  }
  function reorderPhase(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return;
    setPhases(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      // When dragging DOWN, splicing `from` out shifts every later
      // index down by one — so to land the moved phase BEFORE the
      // hovered target (the visual `borderTop` cue), we insert at
      // `to - 1` instead of `to`. Dragging UP needs no adjustment
      // because indices ≤ `to` are untouched by the removal.
      const adjustedTo = from < to ? to - 1 : to;
      next.splice(adjustedTo, 0, moved);
      return next;
    });
  }

  // ── Plan save/load ───────────────────────────────────────────────────────
  async function savePlan() {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const p = await planService.create(saveName.trim(), phases);
      setPlans(curr => [p, ...curr]);
      setCurrentPlanId(p.plan_id);
      setPlanName(p.name);
      setSaveModalOpen(false);
      setSaveName('');
      message.success(t('costSimulator.plan.saved'));
    } catch {
      message.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  }
  function loadPlan(p: SavedPlan) {
    setPhases(p.phases || []);
    setPlanName(p.name);
    setCurrentPlanId(p.plan_id);
    setLoadModalOpen(false);
  }
  async function deletePlan(id: string) {
    try {
      await planService.remove(id);
      setPlans(curr => curr.filter(p => p.plan_id !== id));
      if (currentPlanId === id) {
        setCurrentPlanId(null); setPlanName(''); setPhases([]);
      }
      message.success(t('costSimulator.plan.deleted'));
    } catch {
      message.error(t('common.error'));
    }
  }
  function newPlan() {
    setPhases([]); setPlanName(''); setCurrentPlanId(null); setResult(null);
    writeDraft(null);
  }

  // ── Chart data ───────────────────────────────────────────────────────────
  // Each monthly entry carries both an aggregate `monthlyCost` and the
  // pre-split `breakdownAICost` + `breakdownInfraCost` (already populated
  // by `_compute_phase_breakdown` in plan_engine.py for both Advanced and
  // simple-sim scenarios). We surface BOTH the AI vs Infra split AND the
  // one-time setup so the user can see exactly where each month's bill
  // goes.
  const chartData = useMemo(() => {
    if (!result) return [];
    let cumulative = 0;
    return result.monthly.map((m: any) => {
      cumulative += m.totalThisMonth;
      return {
        month: `M${m.monthIndex + 1}`,
        aiRecurring: Number(m.breakdownAICost ?? 0),
        infraRecurring: Number(m.breakdownInfraCost ?? 0),
        recurring: m.monthlyCost,
        setup: m.setupAppliedThisMonth,
        cumulative,
        phaseLabel: m.phaseLabel,
      };
    });
  }, [result]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header row — plan name + actions */}
      <Card size="small">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Input dir="auto"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              placeholder={t('costSimulator.plan.namePlaceholder')}
              variant="borderless"
              style={{ fontSize: 16, fontWeight: 600, paddingInlineStart: 0 }}
            />
            <div style={{ fontSize: 11, opacity: 0.55 }}>
              {currentPlanId
                ? t('costSimulator.plan.savedPlanHint')
                : t('costSimulator.plan.unsavedHint')}
            </div>
          </div>
          <Button icon={<PlusOutlined />} onClick={newPlan}>{t('costSimulator.plan.new')}</Button>
          <Button icon={<FolderOpenOutlined />} onClick={() => setLoadModalOpen(true)} disabled={plans.length === 0}>
            {t('costSimulator.plan.load')} ({plans.length})
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => { setSaveName(planName || ''); setSaveModalOpen(true); }} disabled={phases.length === 0}>
            {t('costSimulator.plan.save')}
          </Button>
        </div>
      </Card>

      {/* Phases */}
      <Card size="small" title={t('costSimulator.plan.phasesTitle')}>
        {phases.length === 0 ? (
          <Empty
            description={loadingScenarios
              ? <Spin size="small" />
              : (scenarios.length === 0
                  ? t('costSimulator.plan.noScenarios')
                  : t('costSimulator.plan.addPhaseHint'))}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setScenarioPickerOpen(true)}
              disabled={scenarios.length === 0}
            >
              {t('costSimulator.plan.addPhase')}
            </Button>
          </Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {phases.map((phase, idx) => (
              <div
                key={phase.id}
                onDragOver={e => {
                  if (dragIndex == null) return;
                  e.preventDefault();
                  if (overIndex !== idx) setOverIndex(idx);
                }}
                onDrop={e => {
                  e.preventDefault();
                  if (dragIndex != null && dragIndex !== idx) reorderPhase(dragIndex, idx);
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragLeave={() => { if (overIndex === idx) setOverIndex(null); }}
                style={{
                  opacity: dragIndex === idx ? 0.4 : 1,
                  borderTop: overIndex === idx && dragIndex != null && dragIndex !== idx
                    ? `2px solid ${token.colorPrimary}` : '2px solid transparent',
                  transition: 'opacity 0.15s, border-color 0.15s',
                }}
              >
                <PhaseCard
                  index={idx}
                  phase={phase}
                  onUpdate={partial => updatePhase(idx, partial)}
                  onDelete={() => removePhase(idx)}
                  onDragStart={() => setDragIndex(idx)}
                  onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                />
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              onClick={() => setScenarioPickerOpen(true)}
              style={{ width: '100%' }}
            >
              {t('costSimulator.plan.addPhase')}
            </Button>
          </div>
        )}
      </Card>

      {/* KPIs */}
      {result && phases.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <Card size="small">
            <Statistic
              title={t('costSimulator.plan.kpi.total')}
              value={result.totalPlanCost}
              precision={2} prefix="$"
              valueStyle={{ color: token.colorPrimary, fontSize: 22 }}
            />
            <div style={{ fontSize: 10, opacity: 0.55 }}>
              {t('costSimulator.plan.kpi.totalNote', { months: result.durationMonths })}
            </div>
          </Card>
          <Card size="small">
            <Statistic
              title={t('costSimulator.plan.kpi.avgMonthly')}
              value={result.avgMonthly}
              precision={2} prefix="$"
              valueStyle={{ fontSize: 22 }}
            />
          </Card>
          <Card size="small">
            <Statistic
              title={t('costSimulator.plan.kpi.recurring')}
              value={result.totalRecurringCost}
              precision={2} prefix="$"
              valueStyle={{ fontSize: 22 }}
            />
            <div style={{ fontSize: 10, opacity: 0.55 }}>{t('costSimulator.plan.kpi.recurringNote')}</div>
          </Card>
          {result.totalSetupCost > 0 && (
            <Card size="small" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
              <Statistic
                title={<span style={{ color: '#92400e' }}>{t('costSimulator.plan.kpi.setup')}</span>}
                value={result.totalSetupCost}
                precision={2} prefix="$"
                valueStyle={{ color: '#b45309', fontSize: 22 }}
              />
              <div style={{ fontSize: 10, color: '#92400e', opacity: 0.75 }}>
                {t('costSimulator.plan.kpi.setupNote')}
              </div>
            </Card>
          )}
          {result.peakMonth && (
            <Card size="small">
              <Statistic
                title={t('costSimulator.plan.kpi.peak')}
                value={result.peakMonth.totalThisMonth}
                precision={2} prefix="$"
                valueStyle={{ fontSize: 22 }}
              />
              <div style={{ fontSize: 10, opacity: 0.55 }}>
                {t('costSimulator.plan.kpi.peakNote', { month: result.peakMonth.monthIndex + 1 })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Timeline chart */}
      {result && result.monthly.length > 0 && (
        <Card size="small" title={t('costSimulator.plan.chartTitle')}>
          {calculating && <div style={{ fontSize: 11, opacity: 0.5 }}><Spin size="small" /> recalculating…</div>}
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 50, bottom: 4, left: 10 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={v => fmt(v)} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => fmt(v)} tick={{ fontSize: 11 }} />
              <ChartTooltip
                formatter={(v: number, name: string) => [fmt(v), name]}
                labelFormatter={(label: string, items: any[]) => {
                  const ph = items[0]?.payload?.phaseLabel;
                  return ph ? `${label} · ${ph}` : label;
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {/* Stacked recurring split — AI on bottom, Infrastructure on top.
                  These two sum to the monthly recurring; together with the
                  amber Setup bar they reconcile to `totalThisMonth`. */}
              <Bar yAxisId="left" dataKey="aiRecurring"    stackId="a" fill="#6366f1" name={t('costSimulator.aiCostPerMonth')} />
              <Bar yAxisId="left" dataKey="infraRecurring" stackId="a" fill="#64748b" name={t('costSimulator.infraPerMonth')} />
              <Bar yAxisId="left" dataKey="setup"          stackId="a" fill="#f59e0b" name={t('costSimulator.plan.chart.setup')} />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke="#16a34a" strokeWidth={2} dot={false} name={t('costSimulator.plan.chart.cumulative')} />
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 10, opacity: 0.55, marginBlockStart: 4 }}>
            {t('costSimulator.plan.chartHint')}
          </div>
        </Card>
      )}

      {/* Per-phase breakdown table — auto-sized columns + horizontal scroll
          fallback for narrow viewports. Adds AI / Infra columns so the user
          can see the same split as the chart row-by-row. */}
      {result && result.perPhase.length > 0 && (
        <Card size="small" title={t('costSimulator.plan.perPhaseTitle')}>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={result.perPhase.map((p: any, i: number) => ({ ...p, key: `${p.phaseId}-${i}` }))}
            columns={[
              { title: '#', dataIndex: 'phaseId', key: 'n', width: 48,
                render: (_: any, __: any, i: number) => i + 1 },
              { title: t('costSimulator.plan.col.label'), dataIndex: 'label', key: 'label',
                ellipsis: { showTitle: true }, minWidth: 160 } as any,
              { title: t('costSimulator.plan.col.months'), dataIndex: 'durationMonths',
                key: 'months', width: 80, align: 'right' as const },
              { title: t('costSimulator.plan.col.tenants'), dataIndex: 'numTenants',
                key: 'tenants', width: 90, align: 'right' as const },
              { title: t('costSimulator.plan.col.usersPerTenant'), dataIndex: 'usersPerTenant',
                key: 'users', width: 130, align: 'right' as const },
              // New: per-phase AI / Infra split. Sourced from breakdown.
              { title: 'AI / mo', key: 'ai', width: 110, align: 'right' as const,
                render: (_: any, r: any) => {
                  const b = r.breakdown || {};
                  // Advanced engine: totalAICost · Simple sim: ai.subtotal
                  const ai = b.totalAICost ?? b.ai?.subtotal ?? 0;
                  return fmt(Number(ai));
                } },
              { title: t('costSimulator.infraMo'), key: 'infra', width: 110, align: 'right' as const,
                render: (_: any, r: any) => {
                  const b = r.breakdown || {};
                  const infra = b.totalInfrastructureCost ?? b.infra?.subtotal ?? 0;
                  return fmt(Number(infra));
                } },
              { title: t('costSimulator.plan.col.monthly'), dataIndex: 'monthlyRecurring',
                key: 'mo', width: 120, align: 'right' as const,
                render: (v: number) => fmt(v) },
              {
                title: t('costSimulator.plan.col.setup'),
                dataIndex: 'setupOneTime', key: 'setup',
                width: 110, align: 'right' as const,
                render: (v: number, r: any) =>
                  r.setupSkippedReuse
                    ? <Tag color="green" style={{ fontSize: 9 }}>reused</Tag>
                    : (v > 0 ? fmt(v) : '—'),
              },
              { title: t('costSimulator.plan.col.phaseTotal'), dataIndex: 'phaseTotal',
                key: 'total', width: 130, align: 'right' as const,
                render: (v: number) => <strong>{fmt(v)}</strong> },
            ] as any}
          />
        </Card>
      )}

      {/* Scenario picker modal */}
      <Modal
        open={scenarioPickerOpen}
        title={t('costSimulator.plan.pickScenarioTitle')}
        onCancel={() => setScenarioPickerOpen(false)}
        footer={null}
        width={720}
      >
        {scenarios.length === 0 ? (
          <Alert type="info" message={t('costSimulator.plan.noScenariosModal')} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, maxHeight: 460, overflowY: 'auto' }}>
            {scenarios.map(s => (
              <ScenarioCard
                key={s.scenario_id}
                scenario={s}
                pricing={pricing}
                onUseInPlan={() => addPhase(s)}
              />
            ))}
          </div>
        )}
      </Modal>

      {/* Save plan modal */}
      <Modal
        open={saveModalOpen}
        title={t('costSimulator.plan.savePlanTitle')}
        onOk={savePlan}
        onCancel={() => { setSaveModalOpen(false); setSaveName(''); }}
        confirmLoading={saving}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Input dir="auto"
          value={saveName}
          onChange={e => setSaveName(e.target.value)}
          onPressEnter={savePlan}
          placeholder={t('costSimulator.plan.namePlaceholder')}
        />
      </Modal>

      {/* Load plan modal */}
      <Modal
        open={loadModalOpen}
        title={t('costSimulator.plan.loadPlanTitle')}
        onCancel={() => setLoadModalOpen(false)}
        footer={null}
        width={600}
      >
        {plans.length === 0 ? (
          <Empty description={t('costSimulator.plan.noSavedPlans')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plans.map(p => (
              <div key={p.plan_id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: token.colorFillAlter, borderRadius: token.borderRadius,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }} dir="auto">{p.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>
                    {p.phases.length} phases · saved {new Date(p.updated_at).toLocaleDateString(activeLocale())}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="small" icon={<FolderOpenOutlined />} onClick={() => loadPlan(p)}>{t('common.load')}</Button>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deletePlan(p.plan_id)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Phase editor card ───────────────────────────────────────────────────────

function PhaseCard({ index, phase, onUpdate, onDelete, onDragStart, onDragEnd }: {
  index: number;
  phase: PlanPhase;
  onUpdate: (p: Partial<PlanPhase>) => void;
  onDelete: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  return (
    <Card size="small" style={{ background: token.colorFillAlter }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* Drag handle — only the grip is draggable so input fields stay clickable */}
        <div
          draggable
          onDragStart={e => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
            onDragStart?.();
          }}
          onDragEnd={onDragEnd}
          title={t('costSimulator.plan.dragHint')}
          style={{
            cursor: 'grab', alignSelf: 'stretch',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0.45, paddingInline: 2,
          }}
          onMouseDown={e => (e.currentTarget.style.cursor = 'grabbing')}
          onMouseUp={e => (e.currentTarget.style.cursor = 'grab')}
        >
          <HolderOutlined style={{ fontSize: 16 }} />
        </div>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: token.colorPrimary, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>{index + 1}</div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input dir="auto"
              value={phase.label}
              onChange={e => onUpdate({ label: e.target.value })}
              placeholder={t('costSimulator.plan.labelPlaceholder')}
              size="small"
              style={{ fontWeight: 600, flex: 1 }}
            />
            <Tag style={{ fontSize: 10, margin: 0 }}>{phase.scenario_name_at_snapshot}</Tag>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <FieldSlider
              label={t('costSimulator.plan.field.duration')}
              min={1} max={36} step={1} suffix="mo"
              value={phase.duration_months}
              onChange={v => onUpdate({ duration_months: v })}
            />
            <FieldSlider
              label={t('costSimulator.plan.field.tenants')}
              min={1} max={200} step={1}
              value={phase.num_tenants_override}
              onChange={v => onUpdate({ num_tenants_override: v })}
            />
            <FieldSlider
              label={t('costSimulator.plan.field.usersPerTenant')}
              min={1} max={10_000} step={1}
              value={phase.users_per_tenant_override}
              onChange={v => onUpdate({ users_per_tenant_override: v })}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

function FieldSlider({ label, min, max, step, value, onChange, suffix }: {
  label: string; min: number; max: number; step: number; value: number; onChange: (v: number) => void; suffix?: string;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, opacity: 0.65, marginBlockEnd: 2 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Slider min={min} max={max} step={step} value={value} onChange={onChange} style={{ flex: 1, marginInline: 4 }} tooltip={{ formatter: v => `${v}${suffix ? ' ' + suffix : ''}` }} />
        <InputNumber
          min={min} max={max} step={step} value={value}
          onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) onChange(v); }}
          size="small" style={{ width: 80, flexShrink: 0 }}
          formatter={(v) => (v === undefined || v === null || String(v) === '') ? '' : `${v}${suffix ? ' ' + suffix : ''}`}
          parser={s => {
            const cleaned = String(s ?? '').replace(/[^\d.\-]/g, '');
            const n = Number(cleaned);
            return Number.isNaN(n) ? (min as any) : (n as any);
          }}
        />
      </div>
    </div>
  );
}
